import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnDestroy, signal, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { RouterModule } from '@angular/router';
import { FwTooltipDirective } from '@flywheel-io/vision';
import intersection from 'lodash/intersection';
import orderBy from 'lodash/orderBy';
import { BehaviorSubject, combineLatest, merge, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { debounceTime, first, map, startWith, switchMap } from 'rxjs/operators';

import { DirectivesModule } from '../../common/directives.module';
import { ContainerModule } from '../../containers/container.module';
import { ApiParams, CoreApiService, queryAll } from '../../core-api.service';
import { FileModule } from '../../files/file.module';
import { IconService } from '../../icon.service';
import { LoggingService } from '../../logging.service';
import { MaterialModule } from '../../material.module';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { SiteRole } from '../../shared/enums/site-role.enum';
import { Acquisition } from '../../shared/models/acquisition.model';
import { Container } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { Session } from '../../shared/models/session.model';
import { firstValueFrom, replayValue, tryOperations } from '../../shared/observable.utils';
import { PipesModule } from '../../shared/pipes.module';
import { SharedModule } from '../../shared/shared.module';
import { SidebarService } from '../../sidebar.service';
import { AcquisitionFilesComponent } from '../acquisition-files/acquisition-files.component';
import { AcquisitionSortComponent, AcquisitionSortValue } from '../acquisition-sort/acquisition-sort.component';
import { SessionCheckedStatus, SessionService } from '../session.service';
import { SessionDataState } from '../session-data/session-data.state';

type DropTargetAcquisition = Pick<Acquisition, '_id' | 'label' | 'files'>;

const FILE_FIELD_LABELS: Record<string, string> = {
  file: 'File',
  description: 'Name',
  created: 'Created Date',
  classification: 'Classification',
  fileType: 'Type',
};

@Component({
  selector: 'acquisition-list',
  templateUrl: './acquisition-list.component.html',
  styleUrls: ['./acquisition-list.component.scss'],
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwTooltipDirective,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
    AcquisitionFilesComponent,
    AcquisitionSortComponent,
  ],
  providers: [IconService],
})
export class AcquisitionListComponent implements OnChanges, OnDestroy {
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private iconService = inject(IconService);
  private notifications = inject(NotificationService);
  private profileService = inject(ProfileService);
  private sidebar = inject(SidebarService);
  state = inject(SessionDataState);
  sessionService = inject(SessionService);

  @Input() collectionId?: string;
  @Input() session: Session;

  acquisitionFiles = new WeakMap<Acquisition, File[]>(); // filtered files
  checkedStatus = signal<SessionCheckedStatus>(undefined);
  error = signal<Error>(null);
  fileTypes = signal<string[]>([]);
  filterFields = new FormGroup({
    description: new FormControl(''),
    created: new FormControl(''),
    classification: new FormControl(''),
    fileType: new FormControl(''),
  });
  pagination = {
    limit: 50,
    page$: new BehaviorSubject(0), // zero-based
    total: 0,
  };
  onlyAcquisition: string;
  permissions = {
    moveFiles: false,
  };
  sessionAcquisitions$ = new ReplaySubject<Acquisition[]>(1); // unfiltered acquisitions
  sortedAcquisitions$: Observable<Acquisition[]>;
  sortPanel = new FormControl({
    acquisition: this.state.activeAcquisitionSort$.value,
    file: this.state.activeFileSort$.value,
  });
  stagedAcquisition = signal<DropTargetAcquisition>(null);

  private subscriptions = {
    acquisitions: Subscription.EMPTY,
    changes: Subscription.EMPTY,
    checks: Subscription.EMPTY,
    moveFiles: Subscription.EMPTY,
    permissions: Subscription.EMPTY,
    searchFilter: Subscription.EMPTY,
    sortedData: Subscription.EMPTY,
    sortUpdate: Subscription.EMPTY,
  };

  constructor() {
    this.subscriptions.permissions = this.state.projectPermissions$.subscribe((permissions) => {
      this.permissions = {
        moveFiles: permissions.has(PermissionAction.FilesMove),
      };
    });

    combineLatest([
      replayValue(this.filterFields.controls.description),
      replayValue(this.filterFields.controls.created),
      replayValue(this.filterFields.controls.classification),
      replayValue(this.filterFields.controls.fileType),
    ])
      .pipe(
        debounceTime(200),
        map(([description, created, classification, fileType]) => ({ description, created, classification, fileType })),
      )
      .subscribe(this.state.acquisitionFilters$);

    this.sortedAcquisitions$ = combineLatest([
      this.sessionAcquisitions$,
      this.state.activeAcquisitionSort$,
      this.state.activeFileSort$,
      this.state.acquisitionFilters$,
      this.sidebar.search.acqId$,
    ]).pipe(
      switchMap(([acquisitions, acquisitionSort, fileSort, fileFilters, onlyAcquisition]) => {
        if (!acquisitions) {
          return of(null);
        }
        fileFilters = {
          description: fileFilters.description.toLowerCase(),
          classification: fileFilters.classification.toLowerCase(),
          created: fileFilters.created,
          fileType: fileFilters.fileType.toLowerCase(),
        };
        const filtersApplied = Object.values(fileFilters).some((value) => value);
        const filteredAcquisitions = acquisitions.filter((acq) => {
          if (onlyAcquisition && onlyAcquisition !== acq._id) {
            return false;
          }
          const filteredFiles = acq.files.filter((file) => {
            if (fileFilters.description && !file.name.toLowerCase().includes(fileFilters.description)) {
              return false;
            }
            // '' for file type filter means All Types
            if (fileFilters.fileType && (file.type || '').toLowerCase() !== fileFilters.fileType) {
              return false;
            }
            if (fileFilters.created && !file.created.includes(fileFilters.created)) {
              return false;
            }
            const classification = this.sessionService.getFileClassification(file) || '';
            return classification.toLowerCase().includes(fileFilters.classification);
          });
          const sortedFiles =
            fileSort.active && fileSort.direction
              ? orderBy(
                  filteredFiles,
                  (file) => {
                    switch (fileSort.active) {
                      case 'description':
                        return file.name?.toLowerCase();
                      case 'fileType':
                        return file.type?.toLowerCase();
                      case 'classification':
                        return this.sessionService.getFileClassification(file)?.toLowerCase();
                      default:
                        return file[fileSort.active] || file.name?.toLowerCase();
                    }
                  },
                  fileSort.direction,
                )
              : filteredFiles;
          this.acquisitionFiles.set(acq, sortedFiles);
          return Boolean(onlyAcquisition) || !filtersApplied || sortedFiles.length > 0;
        });
        const sortedAcquisitions =
          acquisitionSort.active && acquisitionSort.direction
            ? orderBy(
                filteredAcquisitions,
                // If a value is set to null, return empty string for sorting
                (acq) => (acquisitionSort.active === 'description' ? acq.label : acq[acquisitionSort.active]) ?? '',
                acquisitionSort.direction,
              )
            : filteredAcquisitions;
        if (this.pagination.total !== sortedAcquisitions.length) {
          // reset page index if filters have been applied
          this.pagination.page$.next(0);
        }
        this.pagination.total = sortedAcquisitions.length;
        return this.pagination.page$.pipe(
          map((page) => sortedAcquisitions.slice(page * this.pagination.limit, (page + 1) * this.pagination.limit)),
        );
      }),
    );

    this.subscriptions.sortedData = this.sortedAcquisitions$.subscribe((acquisitions) => {
      // send dicom series/file order
      const dicomAcquisitions = acquisitions?.reduce((dicomAcqs, acq) => {
        const sortedDicoms = this.acquisitionFiles.get(acq)?.filter((file) => file.type === 'dicom') ?? [];
        return sortedDicoms?.length > 0 ? dicomAcqs.concat({ ...acq, files: sortedDicoms }) : dicomAcqs;
      }, []);
      this.state.dicomAcquisitions$.next(dicomAcquisitions ?? []);
    });

    this.subscriptions.changes = merge(this.sessionService.acquisitionChange$, this.state.fileChanges$).subscribe(
      () => {
        this.loadAcquisitions();
      },
    );

    // initialize sort options from local user settings
    this.profileService.settings$.pipe(first()).subscribe((settings) => {
      const [acqsSortValue, acqsSortState] = settings.acquisitionList?.sortAcquisitions || [];
      if (acqsSortValue && acqsSortState) {
        this.state.activeAcquisitionSort$.next({ active: acqsSortValue, direction: acqsSortState });
      }
      const [filesSortValue, filesSortState] = settings.acquisitionList?.sortFiles || [];
      if (filesSortValue && filesSortState) {
        this.state.activeFileSort$.next({ active: filesSortValue, direction: filesSortState });
      }
    });

    // keep sort panel and user settings updated
    this.subscriptions.sortUpdate = combineLatest([
      this.state.activeAcquisitionSort$,
      this.state.activeFileSort$,
    ]).subscribe(([acqSort, fileSort]) => {
      this.sortPanel.setValue(
        {
          acquisition: acqSort,
          file: fileSort,
        },
        { emitEvent: false },
      );
      this.profileService.updateSettings({
        acquisitionList: {
          sortAcquisitions: [acqSort.active, acqSort.direction],
          sortFiles: [fileSort.active, fileSort.direction],
        },
      });
    });

    // propagate sort panel changes
    this.sortPanel.valueChanges.subscribe((acqSort: AcquisitionSortValue) => {
      this.state.activeAcquisitionSort$.next(acqSort.acquisition);
      this.state.activeFileSort$.next(acqSort.file);
    });

    // move files between acquisitions
    this.subscriptions.moveFiles = this.state.fileMove$.subscribe(async (movements) => {
      const acquisitions = await firstValueFrom(this.sessionAcquisitions$);
      const fileIds = new Set(movements.map(({ file }) => file.file_id));
      const moveFiles = <A extends DropTargetAcquisition>(acq: A): A => ({
        ...acq,
        files: [
          ...acq.files.filter((file) => !fileIds.has(file.file_id)),
          ...movements.filter(({ acquisitionId }) => acquisitionId === acq._id).map(({ file }) => file),
        ],
      });
      this.stagedAcquisition.update((staged) => staged && moveFiles(staged));
      this.sessionAcquisitions$.next(acquisitions.map(moveFiles));
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.session &&
      this.session?._id &&
      (changes.session.firstChange || this.session._id !== changes.session.previousValue?._id)
    ) {
      this.subscriptions.checks.unsubscribe();
      this.subscriptions.checks = this.sessionService.checkedSessions$
        .pipe(startWith(this.sessionService.checkedSessions$.value))
        .subscribe((checkedSessions) => {
          this.checkedStatus.set(checkedSessions.get(this.session._id));
        });
      this.loadAcquisitions();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get fileSort(): Sort {
    return this.state.activeFileSort$.value;
  }

  loadAcquisitions(): void {
    this.error.set(null);
    this.sessionAcquisitions$.next(null);
    const baseParams: ApiParams = {
      join: 'origin',
    };
    if (this.collectionId) {
      baseParams.filter = `collections=${this.collectionId}`;
    }
    this.subscriptions.acquisitions.unsubscribe();
    this.subscriptions.acquisitions = this.profileService
      .getProfile()
      .pipe(
        first((data) => Boolean(data)),
        switchMap((profile) => {
          if (SiteRole.isSiteAdmin(profile)) {
            baseParams.exhaustive = true;
          }
          return queryAll((params) =>
            this.coreApi.sessions.acquisitions(this.session._id, { ...baseParams, ...params }),
          );
        }),
      )
      .subscribe({
        next: (results) => {
          const acquisitions = results;
          this.sessionAcquisitions$.next(acquisitions);
          this.session = { ...this.session, acquisitions }; // for checked status

          const fileTypes = new Set<string>();
          for (const acq of acquisitions) {
            for (const file of acq.files) {
              fileTypes.add(file.type || '');
            }
          }
          this.fileTypes.set(orderBy(Array.from(fileTypes), (str) => str.toLowerCase(), 'asc'));

          if (this.checkedStatus()?.acquisitions.size > 0) {
            // Ensure checked acquisitions still exist
            const checkedAcqIds = intersection(
              Array.from(this.checkedStatus().acquisitions),
              acquisitions.map((acq) => acq._id),
            );
            this.sessionService.checkAcquisitions(this.session, Array.from(checkedAcqIds));
          }
        },
        error: (error) => {
          this.error.set(error);
        },
      });
  }

  // trackBy
  getContainerId(index: number, container: Container): string {
    return container._id;
  }

  isAcquisitionChecked(acquisitionId: string): boolean {
    if (!this.checkedStatus()) {
      return false;
    }
    if (this.checkedStatus().checked && !this.checkedStatus().indeterminate) {
      return true;
    }
    return this.checkedStatus().indeterminate && this.checkedStatus().acquisitions.has(acquisitionId);
  }

  checkAcquisition(acquisitionId?: string, checked?: boolean): void {
    if (acquisitionId) {
      const checkedAcqs = this.checkedStatus()?.acquisitions || new Set();
      const sessionChecked = this.checkedStatus()?.checked || false;
      const indeterminate = this.checkedStatus()?.indeterminate || false;
      let acqIds = new Set(checkedAcqs);
      if (typeof checked === 'boolean') {
        acqIds[checked ? 'add' : 'delete'](acquisitionId);
      } else {
        acqIds[acqIds.has(acquisitionId) || (sessionChecked && !indeterminate) ? 'delete' : 'add'](acquisitionId);
      }
      if (acqIds.size === 0 && checkedAcqs.size === 0) {
        // we unchecked from an empty set; check all others
        acqIds = new Set(this.session.acquisitions.map((acq) => acq._id).filter((id) => id !== acquisitionId));
      }
      this.sessionService.checkAcquisitions(this.session, Array.from(acqIds));
    } else {
      this.sessionService.checkSessions([this.session._id], checked);
    }
  }

  changeSort(field: string): void {
    const nextSort = { ...this.fileSort };
    if (field === nextSort.active) {
      nextSort.direction = nextSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      nextSort.active = field;
      nextSort.direction = 'asc';
    }
    this.state.activeFileSort$.next(nextSort);
  }

  getFieldTooltip(field: string): string {
    const fieldLabel = FILE_FIELD_LABELS[field];

    if (this.fileSort.active !== field) {
      return `Sort Files by ${fieldLabel}`;
    }

    const direction = this.fileSort.direction === 'asc' ? 'Ascending' : 'Descending';

    return `Sorting by File ${fieldLabel} in ${direction} order`;
  }

  async dropFiles(event: CdkDragDrop<{ acquisition: Acquisition }>): Promise<void> {
    if (!this.permissions.moveFiles) {
      this.notifications.warning('files.move.permission');
      return;
    }

    const itemFile: File = event.item.data.file;
    let containerAcquisition: DropTargetAcquisition = event.container.data.acquisition;
    if (!containerAcquisition) {
      // if staged acquisition is already available, use that; otherwise initialize it
      containerAcquisition = this.stagedAcquisition() ?? { _id: null, label: '', files: [] };
      this.stagedAcquisition.set(containerAcquisition);
    }

    const sourceFileIds = new Set(await firstValueFrom(this.state.fileSelection$));
    sourceFileIds.add(itemFile.file_id);
    const sessionAcquisitions = await firstValueFrom(this.sessionAcquisitions$);
    const allFiles = new Map<string, File>(
      [...sessionAcquisitions, this.stagedAcquisition()].reduce(
        (files, acq) => files.concat((acq?.files ?? []).map((file) => [file.file_id, file])),
        [],
      ),
    );
    const sourceFiles = Array.from(allFiles.values()).filter((file) => sourceFileIds.has(file.file_id));

    const renameFileIds = new Set<string>();
    const moveFileIds = new Set<string>();
    const targetFilenames = new Set(containerAcquisition.files.map((file) => file.name));
    const localMovements = sourceFiles
      .filter((file) => file.parents.acquisition !== containerAcquisition._id)
      .map((file) => {
        if (targetFilenames.has(file.name)) {
          renameFileIds.add(file.file_id);
        } else {
          moveFileIds.add(file.file_id);
          targetFilenames.add(file.name);
        }
        return { file, acquisitionId: containerAcquisition._id };
      });
    this.state.fileMove$.next(localMovements);
    // clear shift selection
    this.state.fileSelection$.next(new Set());
    // signal rename on conflicted files, after files are settled
    if (renameFileIds.size > 0) {
      setTimeout(() => {
        this.notifications.warning('files.move.rename');
        for (const fileId of renameFileIds) {
          this.state.fileRename$.next(fileId);
        }
      });
    }

    if (moveFileIds.size === 0 || !containerAcquisition._id) {
      return;
    }

    const moveOutcomes = await firstValueFrom(
      tryOperations(Array.from(moveFileIds), (fileId) =>
        this.coreApi.files.move(fileId, {
          container_reference: { type: ContainerType.Acquisition, id: containerAcquisition._id },
          run_gear_rules: this.state.runGearRulesOnMove$.value ?? false,
        }),
      ),
    );
    const movedFiles = moveOutcomes.filter((outcome) => outcome.result).map((outcome) => allFiles.get(outcome.source));
    for (const file of movedFiles) {
      file.parents.acquisition = containerAcquisition._id;
    }
    const failedFileIds = new Set(moveOutcomes.filter((outcome) => outcome.error).map((outcome) => outcome.source));
    if (failedFileIds.size === 0) {
      this.notifications.success('files.move.success');
      return;
    }

    const failedFiles = Array.from(allFiles.values()).filter((file) => failedFileIds.has(file.file_id));
    this.logger.error(moveOutcomes.find((outcome) => outcome.error).error);
    this.notifications.error(movedFiles.length > 0 ? 'files.move.partialError' : 'files.move.error');
    this.state.fileMove$.next(failedFiles.map((file) => ({ file, acquisitionId: file.parents.acquisition })));
  }
}
